import { Controller } from "@hotwired/stimulus"

// Live preview for the admin email-template editor (W3.2).
//
// Debounces edits to the subject/body fields, POSTs the in-progress DRAFT to
// the preview endpoint, and renders the result:
//   - the sanitized HTML body into an <iframe srcdoc> (the iframe isolates the
//     email's CSS/layout from the admin chrome and shows the real email),
//   - the rendered subject (returned in the X-Email-Subject-B64 header) into
//     the subject-preview line.
//
// The body is ALREADY sanitized server-side (Premailer -> SafeListSanitizer);
// the iframe is purely for visual isolation, not a trust boundary.
export default class extends Controller {
  static targets = ["subject", "body", "frame", "subjectPreview", "status"]
  static values = {
    url: String,
    debounce: { type: Number, default: 400 }
  }

  connect() {
    this.timer = null
    // Render once on load so the preview reflects the saved content immediately.
    this.refresh()
  }

  disconnect() {
    if (this.timer) clearTimeout(this.timer)
  }

  // Debounced entry point wired to the textarea/subject `input` events.
  schedule() {
    if (this.timer) clearTimeout(this.timer)
    this.#setStatus("Editing…")
    this.timer = setTimeout(() => this.refresh(), this.debounceValue)
  }

  refresh() {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    const params = new URLSearchParams()
    params.append("subject_template", this.subjectTarget.value)
    params.append("body_markdown", this.bodyTarget.value)

    this.#setStatus("Updating preview…")

    fetch(this.urlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Content-Type": "application/x-www-form-urlencoded",
        "Accept": "text/html"
      },
      body: params.toString()
    }).then(response => {
      if (!response.ok) {
        this.#setStatus("Preview unavailable", true)
        return null
      }
      this.#renderSubject(response.headers.get("X-Email-Subject-B64"))
      return response.text()
    }).then(html => {
      if (html === null) return
      // srcdoc isolates the email document inside the iframe.
      this.frameTarget.srcdoc = html
      this.#setStatus("Preview up to date")
    }).catch(() => {
      this.#setStatus("Preview unavailable", true)
    })
  }

  #renderSubject(encoded) {
    if (!this.hasSubjectPreviewTarget) return
    let subject = ""
    if (encoded) {
      try {
        // atob yields a binary string (one char per UTF-8 byte); map it back to
        // bytes and decode as UTF-8 so a non-ASCII subject (emoji/CJK/accents)
        // round-trips. Replaces the deprecated escape()/decodeURIComponent pair.
        const bytes = Uint8Array.from(window.atob(encoded), c => c.charCodeAt(0))
        subject = new TextDecoder().decode(bytes)
      } catch (e) {
        subject = ""
      }
    }
    this.subjectPreviewTarget.textContent = subject
  }

  #setStatus(text, isError = false) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = text
    this.statusTarget.classList.toggle("text-danger", isError)
    this.statusTarget.classList.toggle("text-muted", !isError)
  }
}
